import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useMatchMedia, breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { BannerNotification, PrimaryButton, Text } from '@frontend/design-system';
import { LocationInformation } from '..';
import { useLocationPortal } from '../../../hooks';
import * as styles from '../../../styles/weave-pay.styles';
import { PortalDisabledTypes, WeavePayLocationInfo } from '../../../types';

interface PortalDisabledPageProps {
  locationSlug: string;
  reason?: PortalDisabledTypes;
}

export const PortalDisabledPage = ({ locationSlug, reason }: PortalDisabledPageProps) => {
  const { t } = useTranslation('payments');
  const { portalData } = useLocationPortal({ locationSlug });
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.min });

  const rateLimitMessaging = t('You have exceeded the allowed number of payment attempts');
  const obpDisabledMessaging = t('Our Online Bill Pay service is temporarily unavailable');

  const [locationInfo, setLocationInfo] = useState<Partial<WeavePayLocationInfo>>({
    email: '',
    name: '',
    phoneNumber: '',
  });

  useEffect(() => {
    getLocationData();
  }, [portalData]);

  const getLocationData = () => {
    const headerInfo = {
      email: portalData?.email,
      name: portalData?.name,
      phoneNumber: portalData?.phoneNumber,
    };
    setLocationInfo(headerInfo);
  };

  const getBannerMessaging = () => {
    switch (reason) {
      case PortalDisabledTypes.RateLimit:
        return rateLimitMessaging;
      case PortalDisabledTypes.Disabled:
        return obpDisabledMessaging;
      default:
        return obpDisabledMessaging;
    }
  };

  return (
    <div css={styles.pageContainer}>
      <div css={styles.portalContainer}>
        <BannerNotification
          status='error'
          message={getBannerMessaging()}
          css={css`
            margin-bottom: ${theme.spacing(2)};
          `}
        />
        <div css={styles.portalHeader}>
          {locationInfo && (
            <LocationInformation
              name={locationInfo.name ?? ''}
              email={locationInfo.email ?? ''}
              phoneNumber={locationInfo.phoneNumber ?? ''}
            />
          )}
        </div>
        <Text>
          {t(
            `To pay your bill please send us a text message at ${formatPhoneNumber(
              locationInfo.phoneNumber,
              true,
              true
            )} with the payment amount you would like to make, so we can send you a Text to Pay request. Alternatively, you can call us to make a payment.`
          )}
        </Text>
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: start;
            width: max-content;
            gap: ${theme.spacing(2)};
          `}
        >
          {isMobile && (
            <PrimaryButton
              onClick={() => (window.location.href = `sms:${locationInfo.phoneNumber}`)}
              css={css`
                white-space: nowrap;
              `}
            >
              <Icon
                name='message'
                color='white'
                css={css`
                  margin-right: ${theme.spacing(1)};
                `}
              />
              {t('Text Us')}
            </PrimaryButton>
          )}
          <PrimaryButton onClick={() => (window.location.href = `tel:${locationInfo.phoneNumber}`)}>
            <Icon
              name='phone'
              color='white'
              css={css`
                margin-right: ${theme.spacing(1)};
              `}
            />
            {t('Call Us')}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};
